import axios from 'axios'

const getSubdomain = () => {
  const hostname = window.location.hostname

  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === 'ambunet-build'
  ) {
    return 'localhost'
  }

  return hostname.split('.')[0]
}

const isAdminCRM = window.location.hostname.startsWith('crmadmin')

// Local dev on a tenant subdomain (mytenant.localhost): address the API on
// the same hostname so the session cookie is same-site - Chrome treats
// mytenant.localhost and localhost as different sites, so cookies set by
// localhost:4051 would otherwise never be sent and every authenticated
// request would 401.
const getBaseURL = () => {
  const configured = process.env.REACT_APP_API_URL
  const { hostname } = window.location
  if (configured?.includes('//localhost') && hostname.endsWith('.localhost')) {
    return configured.replace('//localhost', `//${hostname}`)
  }
  return configured
}

const API = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  validateStatus: () => true,
  // validateStatus: (status) => status >= 200 && status < 300,
  timeout: 60000,
  headers: {
    ...(isAdminCRM
      ? { 'X-AmbuNet-App': 'admin-crm' }
      : { 'X-Tenant-Subdomain': getSubdomain() }),
    Pragma: 'no-cache',
    Expires: '0',
    'Content-Type': 'application/json',
  },
})

let globalApiErrorHandler = null

export const setGlobalApiErrorHandler = (handler) => {
  globalApiErrorHandler = handler
}

// For callers that send skipGlobalErrorHandler to deal with one specific
// refusal themselves: hands any other failure to the global handler exactly
// as an ordinary call would have. Pass `{ response: res }` for a resolved
// response with status >= 400, or the axios error from a catch. Returns true
// when the global handler took it (the caller should then stop - the global
// UI, e.g. the Not Authorised screen, is already showing).
export const runGlobalApiErrorHandler = (error) => {
  if (!globalApiErrorHandler || !error) return false
  return !!globalApiErrorHandler(error)
}

API.interceptors.request.use((config) => {
  config.metadata = { startTime: Date.now() }
  return config
})

API.interceptors.request.use((config) => {
  const url = config.url || ''

  const isS3Request =
    url.includes('.s3.') ||
    url.includes('.s3.amazonaws.com') ||
    url.includes('s3.eu-west-') ||
    url.includes('amazonaws.com')

  if (isS3Request) {
    config.withCredentials = false

    delete config.headers?.Authorization
    delete config.headers?.['X-AmbuNet-App']
    delete config.headers?.['X-Tenant-Subdomain']
    delete config.headers?.Pragma
    delete config.headers?.Expires
  }

  return config
})

API.interceptors.response.use(
  (response) => {
    const duration = Date.now() - response.config.metadata.startTime

    if (duration > 10000) {
      window.dispatchEvent(new Event('ambunet:slow-connection'))
    }

    return response
  },
  (error) => {
    if (error?.code === 'ECONNABORTED') {
      window.dispatchEvent(new Event('ambunet:slow-connection'))
    }

    return Promise.reject(error)
  },
)

API.interceptors.response.use(
  (response) => {
    const status = response?.status

    // Handle non-success HTTP statuses globally
    if (
      status >= 400 &&
      globalApiErrorHandler &&
      !response.config?.skipGlobalErrorHandler
    ) {
      // The response is still resolved to the caller even when handled -
      // a promise that never settles leaves dialogs stuck on their busy
      // state (spinning "Issuing..." buttons over the Not Authorised
      // screen). Callers check res.status, so they reset themselves.
      globalApiErrorHandler({ response })
    }

    return response
  },

  (error) => {
    // Network errors / timeouts / offline - same deal: hand it to the
    // global handler for the shared UI, but still reject so the caller's
    // catch can reset its own state.
    if (globalApiErrorHandler && !error.config?.skipGlobalErrorHandler) {
      globalApiErrorHandler(error)
    }

    if (error?.code === 'ECONNABORTED') {
      window.dispatchEvent(new Event('ambunet:slow-connection'))
    }

    return Promise.reject(error)
  },
)

export default API
